import { useEffect, useRef } from "react";
import { gsap } from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";

gsap.registerPlugin(ScrollTrigger);

export default function SceneInsideR() {
  const root = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const el = root.current;
    if (!el) return;

    const ctx = gsap.context(() => {
      const tl = gsap.timeline({
        scrollTrigger: {
          trigger: el,
          start: "top top",
          end: "+=200%",
          scrub: 1,
          pin: true,
        },
      });

      // diagonal planes drift
      tl.from(".plane-a", { xPercent: -60, yPercent: -40, rotate: -8, duration: 1 }, 0);
      tl.from(".plane-b", { xPercent: 80, yPercent: 30, rotate: 6, duration: 1 }, 0);
      tl.from(".plane-c", { xPercent: -30, yPercent: 80, rotate: -4, duration: 1 }, 0);

      // words assemble one by one
      const words = [".word-think", ".word-beyond", ".word-tutorial", ".word-production"];
      words.forEach((w, i) => {
        tl.fromTo(
          w,
          { yPercent: 120, opacity: 0, skewY: 6 },
          { yPercent: 0, opacity: 1, skewY: 0, duration: 0.5, ease: "power3.out" },
          0.4 + i * 0.35,
        );
      });

      // measurement grid grows
      tl.from(".measure-line", {
        scaleX: 0,
        transformOrigin: "left center",
        stagger: 0.02,
        duration: 0.4,
      }, 0.3);

      // R diagonal transforms into horizontal line
      tl.to(".diagonal", {
        rotate: 0,
        width: "120%",
        left: "-10%",
        top: "70%",
        duration: 1,
        ease: "power2.inOut",
      }, 1.8);

      // words fade slightly on exit
      tl.to(".inside-content", {
        yPercent: -12,
        opacity: 0.15,
        duration: 0.5,
      }, 2.2);
    }, root);

    return () => ctx.revert();
  }, []);

  return (
    <section
      id="scene-about"
      ref={root}
      data-theme="dark"
      className="relative h-screen w-full overflow-hidden ink-bg grain"
    >
      {/* diagonal planes */}
      <div className="plane-a absolute top-[10%] left-[5%] w-[35vw] h-[70vh] border border-[color:var(--dark)]"
           style={{ transform: "skewX(-12deg)" }} />
      <div className="plane-b absolute bottom-[8%] right-[6%] w-[28vw] h-[55vh] border border-[color:var(--dark)]"
           style={{ transform: "skewX(-12deg)" }} />
      <div className="plane-c absolute top-[30%] left-[35%] w-[18vw] h-[35vh] bg-[color:var(--dark)]/40" />

      {/* fragment: cropped R silhouette at bottom */}
      <div className="absolute -bottom-[40vh] -right-[10vw] type-display text-[color:var(--dark)] pointer-events-none"
           style={{ fontSize: "80vw", lineHeight: 0.7 }}>
        R
      </div>

      {/* diagonal that will become horizontal line */}
      <div className="diagonal absolute top-[20%] left-[10%] w-[70%] h-[2px] bg-[color:var(--signal)] origin-left"
           style={{ transform: "rotate(28deg)" }} />

      {/* measurement lines */}
      <div className="absolute inset-0 pointer-events-none">
        {Array.from({ length: 12 }).map((_, i) => (
          <div
            key={i}
            className="measure-line absolute left-6 h-[1px] bg-[color:var(--gray)]/20"
            style={{ top: `${(i + 1) * 7}%`, width: `${20 + i * 5}%` }}
          />
        ))}
      </div>

      {/* coordinates */}
      <div className="absolute top-24 left-6 md:left-10 type-mono text-[10px] text-[color:var(--gray)]">
        <div>X: 0428.19</div>
        <div>Y: 0912.44</div>
        <div>Z: -0000.02</div>
      </div>

      <div className="absolute top-24 right-6 md:right-10 type-mono text-[10px] text-[color:var(--gray)] text-right">
        <div>SCENE 02 / INSIDE</div>
        <div className="mt-1">FRAME 018</div>
      </div>

      {/* words */}
      <div className="inside-content absolute inset-0 flex flex-col items-start justify-center px-6 md:px-16 gap-2">
        <div className="word-think type-display text-[color:var(--paper)]" style={{ fontSize: "clamp(4rem, 12vw, 14rem)" }}>
          THINK
        </div>
        <div className="word-beyond type-display text-[color:var(--gray)] ml-[10vw]" style={{ fontSize: "clamp(3rem, 9vw, 10rem)" }}>
          BEYOND
        </div>
        <div className="word-tutorial type-display text-[color:var(--paper)] ml-[24vw]" style={{ fontSize: "clamp(3rem, 9vw, 10rem)" }}>
          THE TUTORIAL
        </div>
        <div className="word-production type-display text-[color:var(--signal)] ml-[6vw]" style={{ fontSize: "clamp(4rem, 12vw, 14rem)" }}>
          INTO PRODUCTION.
        </div>
      </div>

      <div className="absolute bottom-6 left-0 right-0 px-6 md:px-10 flex items-end justify-between">
        <div className="type-label text-[color:var(--gray)]">FRAGMENT / 02</div>
        <div className="type-label text-[color:var(--gray)]">DEPTH · CONTROLLED</div>
      </div>
    </section>
  );
}